import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Upload, ImagePlus, Youtube, HelpCircle,
  PlusCircle, Trash, Loader2, Code, Eye, X, Lock
} from "lucide-react";
import { toast } from "sonner";
import pb from "../../lib/pocketbaseClient";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ---------- Slug generator ----------
const generateSlug = (title) => {
  const base = title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return base + "-" + Date.now();
};

// ---------- Categories ----------
const BLOG_CATEGORIES = [
  "Architecture", "Interior Design", "House Plans", "Construction Tips",
  "Vastu", "Home Decoration", "Real Estate", "Civil Engineering",
  "Building Materials", "News"
];

// ---------- Quill toolbar ----------
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "blockquote", "code-block"],
    ["clean"],
  ],
};

// ---------- Helper to parse FAQ ----------
const parseFaq = (faqStr) => {
  if (!faqStr) return [];
  try { return JSON.parse(faqStr); } catch { return []; }
};

const BlogFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(isEditing);
  const [editorMode, setEditorMode] = useState("rich"); // "rich" | "html"
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    category: "Architecture",
    status: "draft",
    content: "",
    contentFormat: "rich", // ⭐ locked format — html বা rich
    publishDate: new Date().toISOString().slice(0, 10),
    featuredImage: null,
    featuredImagePreview: null,
    bodyImages: [],
    bodyImageUrls: [],
    youtubeUrl: "",
    faq: [],
    tags: "",
    seoTitle: "",
    metaDescription: "",
  });

  // Load blog data if editing
  useEffect(() => {
    if (isEditing) {
      const fetchBlog = async () => {
        try {
          const record = await pb.collection("blogs").getOne(id);
          const savedFormat = record.contentFormat === "html" ? "html" : "rich";

          // ⭐ Lock editor to saved format
          setEditorMode(savedFormat);

          setForm({
            title: record.title || "",
            slug: record.slug || "",
            category: record.category || "Architecture",
            status: record.status || "draft",
            content: record.content || "",
            contentFormat: savedFormat,
            publishDate: record.publishDate || new Date().toISOString().slice(0, 10),
            featuredImage: null,
            featuredImagePreview: record.featuredImage ? pb.files.getUrl(record, record.featuredImage) : null,
            bodyImages: [],
            bodyImageUrls: record.bodyImages ? record.bodyImages.map((f) => pb.files.getUrl(record, f)) : [],
            youtubeUrl: record.youtubeUrl || "",
            faq: parseFaq(record.faq),
            tags: record.tags || "",
            seoTitle: record.seoTitle || "",
            metaDescription: record.metaDescription || "",
          });
        } catch (err) {
          toast.error("Blog not found");
          navigate("/admin/blogs");
        } finally {
          setLoading(false);
        }
      };
      fetchBlog();
    }
  }, [id, isEditing, navigate]);

  // ---------- Handlers ----------
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file" && name === "featuredImage") {
      const file = files?.[0] || null;
      setForm((prev) => ({
        ...prev,
        featuredImage: file,
        featuredImagePreview: file ? URL.createObjectURL(file) : null,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (value) => {
    setForm((prev) => ({ ...prev, content: value }));
  };

  const handleSelectChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Editor mode switch — user clicks, format change korte parbe
  const switchEditorMode = (mode) => {
    setEditorMode(mode);
    setForm((prev) => ({ ...prev, contentFormat: mode }));
  };

  const handleBodyImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const newBodyImages = [...form.bodyImages];
    const newPreviewUrls = [...form.bodyImageUrls];
    files.forEach((file) => {
      newBodyImages.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    });
    setForm((prev) => ({
      ...prev,
      bodyImages: newBodyImages,
      bodyImageUrls: newPreviewUrls,
    }));
  };

  const removeBodyImage = (index) => {
    const updatedImages = [...form.bodyImages];
    const updatedUrls = [...form.bodyImageUrls];
    updatedImages.splice(index, 1);
    updatedUrls.splice(index, 1);
    setForm((prev) => ({
      ...prev,
      bodyImages: updatedImages,
      bodyImageUrls: updatedUrls,
    }));
  };

  const addFaq = () => {
    setForm((prev) => ({
      ...prev,
      faq: [...prev.faq, { question: "", answer: "" }],
    }));
  };
  const removeFaq = (index) => {
    const updated = [...form.faq];
    updated.splice(index, 1);
    setForm((prev) => ({ ...prev, faq: updated }));
  };
  const updateFaq = (index, field, value) => {
    const updated = [...form.faq];
    updated[index][field] = value;
    setForm((prev) => ({ ...prev, faq: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      // Tags normalize: "modern, villa" → "modern,villa"
      const normalizedTags = (form.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .join(",");

      const data = {
        title: form.title.trim(),
        slug: form.slug.trim() || generateSlug(form.title),
        category: form.category,
        status: form.status,
        content: form.content,
        contentFormat: form.contentFormat || "rich",  // ⭐ save format
        publishDate: form.publishDate,
        youtubeUrl: form.youtubeUrl,
        faq: JSON.stringify(form.faq),
        tags: normalizedTags,
        seoTitle: form.seoTitle.trim(),
        metaDescription: form.metaDescription.trim(),
      };

      let recordId;
      if (isEditing) {
        await pb.collection("blogs").update(id, data);
        recordId = id;
      } else {
        const record = await pb.collection("blogs").create(data);
        recordId = record.id;
      }

      // Featured image
      if (form.featuredImage instanceof File) {
        const fd = new FormData();
        fd.append("featuredImage", form.featuredImage);
        await pb.collection("blogs").update(recordId, fd);
      }

      // Body images
      if (form.bodyImages.length > 0) {
        const fd = new FormData();
        form.bodyImages.forEach((file) => {
          fd.append("bodyImages+", file);
        });
        await pb.collection("blogs").update(recordId, fd);
      }

      toast.success(isEditing ? "Blog updated" : "Blog created");
      navigate("/admin/blogs");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response && err.response.data) {
        toast.error("Save failed: " + JSON.stringify(err.response.data));
      } else {
        toast.error(err.message || "Failed to save blog");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }


  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/blogs" className="p-2 hover:bg-slate-100 rounded-xl transition">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEditing ? "Edit Blog Post" : "New Blog Post"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Title & Slug */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input type="text" name="title" value={form.title} onChange={handleChange} required
              placeholder="Enter blog title"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <input type="text" name="slug" value={form.slug} onChange={handleChange}
              placeholder="Auto-generated (unique)"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
          </div>
        </div>

        {/* Category, Status, Date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select value={form.category} onChange={(e) => handleSelectChange("category", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none">
              {BLOG_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select value={form.status} onChange={(e) => handleSelectChange("status", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Publish Date</label>
            <input type="date" name="publishDate" value={form.publishDate} onChange={handleChange}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
          </div>
        </div>

        {/* Content */}
        <div>
          <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
            <label className="block text-sm font-medium">Content</label>
            <div className="flex gap-2">
              <button type="button"
                className={`px-3 py-1 text-sm rounded-xl transition ${editorMode === "rich" ? "bg-indigo-600 text-white" : "border border-slate-200 hover:bg-slate-50"}`}
                onClick={() => switchEditorMode("rich")}>
                <Eye className="w-4 h-4 inline mr-1" /> Rich Text
              </button>
              <button type="button"
                className={`px-3 py-1 text-sm rounded-xl transition ${editorMode === "html" ? "bg-indigo-600 text-white" : "border border-slate-200 hover:bg-slate-50"}`}
                onClick={() => switchEditorMode("html")}>
                <Code className="w-4 h-4 inline mr-1" /> HTML
              </button>
            </div>
          </div>
          <div className="mt-1.5">
            {editorMode === "rich" ? (
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={handleContentChange}
                modules={quillModules}
                placeholder="Write your blog post..."
                className="h-64 mb-12"
              />
            ) : (
              <textarea
                name="content"
                rows={20}
                value={form.content}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none font-mono text-sm"
                placeholder="<h2>Heading</h2>&#10;<p>Your HTML here...</p>"
              />
            )}
          </div>
        </div>

        {/* Media */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <ImagePlus className="w-5 h-5 text-indigo-500" /> Images & Media
          </h3>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Featured Image</label>
            <div className="mt-2">
              {form.featuredImagePreview ? (
                <div className="relative inline-block">
                  <img src={form.featuredImagePreview} alt="Featured" className="h-40 rounded-lg object-cover border" />
                  <button type="button"
                    onClick={() => setForm((prev) => ({ ...prev, featuredImage: null, featuredImagePreview: null }))}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer hover:bg-slate-50">
                  <Upload className="w-6 h-6 text-slate-400 mb-1" />
                  <span className="text-sm text-slate-500">Click to upload</span>
                  <input type="file" name="featuredImage" accept="image/*" className="hidden" onChange={handleChange} />
                </label>
              )}
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Body Images</label>
            <div className="mt-2 space-y-3">
              <div className="flex flex-wrap gap-3">
                {form.bodyImageUrls.map((url, idx) => (
                  <div key={idx} className="relative">
                    <img src={url} alt="" className="h-24 w-24 object-cover rounded-lg border" />
                    <button type="button" onClick={() => removeBodyImage(idx)}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-indigo-600 hover:underline">
                <PlusCircle className="w-4 h-4" /> Add images
                <input type="file" multiple accept="image/*" className="hidden" onChange={handleBodyImagesChange} />
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Youtube className="w-4 h-4 text-red-500" /> YouTube Video URL
            </label>
            <input type="text" name="youtubeUrl" value={form.youtubeUrl} onChange={handleChange}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
          </div>
        </div>

        {/* FAQ */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-indigo-500" /> FAQ
          </h3>
          {form.faq.map((item, idx) => (
            <div key={idx} className="mb-4 p-4 border rounded-xl bg-slate-50 relative">
              <button type="button" onClick={() => removeFaq(idx)}
                className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-100 rounded-full">
                <Trash className="w-4 h-4" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium">Question</label>
                  <input value={item.question} onChange={(e) => updateFaq(idx, "question", e.target.value)}
                    placeholder="Enter question"
                    className="w-full px-3 py-1.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium">Answer</label>
                  <input value={item.answer} onChange={(e) => updateFaq(idx, "answer", e.target.value)}
                    placeholder="Enter answer"
                    className="w-full px-3 py-1.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none mt-1" />
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={addFaq}
            className="flex items-center gap-2 px-4 py-2 border rounded-xl hover:bg-slate-50">
            <PlusCircle className="w-4 h-4" /> Add FAQ
          </button>
        </div>

        {/* SEO & Tags */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-lg mb-4">SEO & Meta</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">SEO Title</label>
              <input type="text" name="seoTitle" value={form.seoTitle} onChange={handleChange}
                placeholder="SEO title"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Tags <span className="text-slate-400 font-normal">(কমা দিয়ে একসাথে সব লিখুন)</span>
              </label>
              <input type="text" name="tags" value={form.tags} onChange={handleChange}
                placeholder="modern, villa, 3bhk, kolkata, duplex"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
              {form.tags && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.tags.split(",").map((t) => t.trim()).filter(Boolean).map((tag, i) => (
                    <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Meta Description</label>
              <textarea name="metaDescription" rows={2} value={form.metaDescription} onChange={handleChange}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Link to="/admin/blogs" className="px-4 py-2 border rounded-xl hover:bg-slate-50">
            Cancel
          </Link>
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {saving ? "Saving..." : isEditing ? "Update Blog" : "Create Blog"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogFormPage;
